// src/components/ui/ViewToggle.tsx
import React from 'react';
import { ViewType } from '../../types';

interface ViewToggleProps {
  currentView: ViewType;
  onViewChange: (view: ViewType) => void;
  projectPeriodType?: 'monthly' | 'quarterly' | 'annual';
  className?: string;
}

export const ViewToggle: React.FC<ViewToggleProps> = ({ 
  currentView, 
  onViewChange, 
  projectPeriodType = 'monthly',
  className = ''
}) => {
  // DEBUG: Log what projectPeriodType we're receiving with more detail
  console.log('🎯 [ViewToggle] Rendered with:', {
    projectPeriodType,
    typeof_projectPeriodType: typeof projectPeriodType,
    currentView,
    timestamp: new Date().toISOString(),
    isUndefined: projectPeriodType === undefined
  });
  // Helper function to get available views based on period type
  const getAvailableViews = (periodType: string): ViewType[] => {
    switch (periodType) {
      case 'quarterly':
        return ['quarterly', 'yearly'];
      case 'annual':
        return ['yearly'];
      default: // monthly
        return ['monthly', 'quarterly', 'yearly'];
    }
  };

  // Helper function to get disabled views
  const getDisabledViews = (periodType: string): ViewType[] => {
    switch (periodType) {
      case 'quarterly':
        return ['monthly']; // Can't show monthly view for quarterly data
      case 'annual':
        return ['monthly', 'quarterly']; // Can only show yearly view for annual data
      default:
        return []; // Monthly data can show all views
    }
  };

  // Helper function to get tooltip text for disabled views
  const getDisabledTooltip = (view: ViewType, periodType: string): string => {
    switch (periodType) {
      case 'quarterly':
        return view === 'monthly' 
          ? 'Monthly view not available for quarterly data' 
          : '';
      case 'annual':
        return view === 'monthly' 
          ? 'Monthly view not available for annual data'
          : view === 'quarterly'
          ? 'Quarterly view not available for annual data'
          : '';
      default:
        return '';
    }
  };

  const availableViews = getAvailableViews(projectPeriodType);
  const disabledViews = getDisabledViews(projectPeriodType);

  // Auto-select first available view if current view is disabled
  React.useEffect(() => {
    if (disabledViews.includes(currentView) && availableViews.length > 0) {
      onViewChange(availableViews[0]);
    }
  }, [projectPeriodType, currentView, availableViews, disabledViews, onViewChange]);

  const views: { key: ViewType; label: string }[] = [
    { key: 'monthly', label: 'Monthly' },
    { key: 'quarterly', label: 'Quarterly' },
    { key: 'yearly', label: 'Yearly' }
  ];

  return (
    <div className={`flex space-x-1 ${className}`}>
      {views.map(({ key, label }) => {
        const isDisabled = disabledViews.includes(key);
        const isActive = currentView === key;
        const tooltipText = isDisabled ? getDisabledTooltip(key, projectPeriodType) : '';

        return (
          <button
            key={key}
            onClick={() => !isDisabled && onViewChange(key)}
            disabled={isDisabled}
            title={tooltipText}
            className={`
              px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200
              ${isActive && !isDisabled
                ? 'bg-blue-600 text-white shadow-sm'
                : isDisabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
              }
              ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            {label}
          </button>
        );
      })}
      
      {/* Period type indicator */}
      {projectPeriodType !== 'monthly' && (
        <div className="flex items-center ml-3 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
          <span className="font-medium">
            {projectPeriodType.charAt(0).toUpperCase() + projectPeriodType.slice(1)} Data
          </span>
        </div>
      )}
    </div>
  );
};
